import { useState } from "react";
import produce from "immer";
import { each, has, isNil, mergeWith } from "lodash";
import { PeriodMath } from "@linkfluence/models/date";
import { setNewTimezone } from "@tootools/date";
import { useCurrentUser } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { WizardTemplate } from "@radarly/models/insight-pages/wizard/model";
import {
  WizardConfiguration,
  WizardEditionSection,
} from "@radarly/models/insight-pages/wizard/model/configuration";

export function updateNewConfiguration(
  configuration: WizardConfiguration,
  update: Partial<WizardConfiguration>
) {
  return produce(configuration, (draft) => {
    each(update, (value, field) => {
      if (field === "fields") {
        draft[field] = mergeWith(draft[field], value, function (obj, src) {
          if (!isNil(src)) {
            return src;
          }
          return obj;
        });
      } else {
        draft[field] = value;
      }
    });
    if (has(update, "timezone")) {
      const existingPeriod = has(update, "period")
        ? update.period
        : draft.period;
      if (existingPeriod) {
        const previousTimezone = configuration.timezone || "UTC";
        draft.period = {
          from: setNewTimezone(
            existingPeriod.from,
            previousTimezone,
            update.timezone
          ),
          to: setNewTimezone(
            existingPeriod.to,
            previousTimezone,
            update.timezone
          ),
        };
      }
    }
  });
}

export function useWizardEditor(template: WizardTemplate) {
  const user = useCurrentUser();
  const [activeSection, setActiveSection] =
    useState<WizardEditionSection | null>(null);
  const [configuration, setConfiguration] = useState<WizardConfiguration>({
    title: tr(user.locale, "INSIGHT_PAGE_TEMPLATE_" + template.id + "::TITLE"),
    includeAIAnalysis: true,
    timezone: user.timezone,
    locale: user.locale,
    period: PeriodMath.Last_30_Days,
    fields: {
      query: null,
      queries: null,
      competitors: null,
      owned: {
        me: false,
        accounts: [],
        corpora: [],
      },
      earned: false,
      influencers: {
        me: false,
        accounts: [],
        corpora: [],
      },
    },
  });
  const updateConfiguration = (update: Partial<WizardConfiguration>) => {
    const newConf = updateNewConfiguration(configuration, update);
    setConfiguration(newConf);
  };
  return {
    configuration,
    updateConfiguration,
    activeSection,
    setActiveSection,
  };
}
